/**
 * Éditeur CEO de l'« Espace Administrateur » : configuration COMMUNE + surcharge PAR PERSONNE
 * (pages et tâches différentes par administrateur). Réservé au propriétaire.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, CircleNotch, FloppyDisk, UserCircle, UsersThree } from "@phosphor-icons/react";
import { NAV_GROUPS } from "../pages/DashboardLayout";

const EMPTY = { title: "Espace Administrateur", welcome_message: "", tasks: [], allowed_paths: [] };

export function AdminSpaceEditor({ isOwner }) {
  const [common, setCommon] = useState(null);
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState("common"); // "common" | email
  const [cfg, setCfg] = useState(null);
  const [useCommon, setUseCommon] = useState(true);
  const [tasksText, setTasksText] = useState("");
  const [saving, setSaving] = useState(false);

  const applyCfg = (c) => { setCfg({ ...EMPTY, ...c }); setTasksText((c.tasks || []).map((t) => t.text).join("\n")); };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/auth/admin-space");
      setCommon(r.data);
      if (isOwner) { const u = await api.get("/auth/admin-space/users"); setUsers(u.data.users || []); }
      return r.data;
    } catch { return null; }
  }, [isOwner]);

  useEffect(() => { load().then((c) => c && applyCfg(c)); }, [load]);

  const selectTarget = (t) => {
    setTarget(t);
    if (t === "common") { setUseCommon(true); applyCfg(common); return; }
    const u = users.find((x) => x.email === t);
    const ov = u?.override || {};
    setUseCommon(u ? u.use_common : true);
    applyCfg(Object.keys(ov).length ? { ...common, ...ov } : common);
  };

  if (!cfg) return null;
  const allowed = new Set(cfg.allowed_paths || []);
  const editable = isOwner && (target === "common" || !useCommon);
  const togglePath = (to) => { const n = new Set(allowed); n.has(to) ? n.delete(to) : n.add(to); setCfg({ ...cfg, allowed_paths: [...n] }); };
  const toggleGroup = (g, on) => { const n = new Set(allowed); g.items.forEach((it) => (on ? n.add(it.to) : n.delete(it.to))); setCfg({ ...cfg, allowed_paths: [...n] }); };

  const save = async () => {
    setSaving(true);
    try {
      const oldTasks = cfg.tasks || [];
      const tasks = tasksText.split("\n").map((s) => s.trim()).filter(Boolean).map((text) => ({ id: oldTasks.find((t) => t.text === text)?.id, text }));
      const body = { title: cfg.title, welcome_message: cfg.welcome_message, tasks, allowed_paths: cfg.allowed_paths };
      if (target === "common") {
        const { data } = await api.put("/auth/admin-space", body);
        setCommon(data); applyCfg(data);
        toast.success(`Configuration commune enregistrée · ${(data.allowed_paths || []).length} page(s).`);
      } else {
        await api.put(`/auth/admin-space/users/${encodeURIComponent(target)}`, { ...body, use_common: useCommon });
        toast.success(useCommon ? `${target} suit la configuration commune.` : `Espace personnalisé enregistré pour ${target}.`);
        const u = await api.get("/auth/admin-space/users"); setUsers(u.data.users || []);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <section className="rounded-2xl border border-amber-500/30 bg-zinc-900/60 p-5 mb-6" data-testid="admin-space-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2"><ShieldCheck size={20} weight="duotone" className="text-amber-400" /> Espace Administrateur (page d'accueil des admins)</h2>
          <p className="text-xs text-zinc-400 mt-1">Les administrateurs ne voient <b>que</b> cette page + les pages cochées. Configuration <b>commune</b>, ou <b>personnalisée par administrateur</b>. Toi seul (CEO) gardes l'accès complet.</p>
        </div>
        {isOwner && (
          <button onClick={save} disabled={saving} data-testid="admin-space-save-btn"
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
            {saving ? <CircleNotch className="animate-spin" size={16} /> : <FloppyDisk size={16} weight="fill" />} Enregistrer
          </button>
        )}
      </div>
      {!isOwner && <div className="text-xs text-amber-300 mb-3">Lecture seule — seul le propriétaire peut modifier cet espace.</div>}

      {isOwner && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-space-targets">
          <button type="button" onClick={() => selectTarget("common")} data-testid="admin-space-target-common"
            className={`px-3 py-1.5 rounded-full text-xs border flex items-center gap-1.5 ${target === "common" ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
            <UsersThree size={14} /> Configuration commune
          </button>
          {users.map((u) => (
            <button key={u.email} type="button" onClick={() => selectTarget(u.email)} data-testid={`admin-space-target-${u.email}`}
              className={`px-3 py-1.5 rounded-full text-xs border flex items-center gap-1.5 ${target === u.email ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
              <UserCircle size={14} /> {u.full_name} {!u.use_common && <span className="text-[9px] px-1 rounded bg-sky-500/20 text-sky-300">perso</span>}
            </button>
          ))}
          {users.length === 0 && <span className="text-xs text-zinc-500 self-center">Aucun administrateur ajouté pour l'instant.</span>}
        </div>
      )}
      {target !== "common" && isOwner && (
        <label className="flex items-center gap-2 text-sm text-zinc-200 mb-4 cursor-pointer" data-testid="admin-space-use-common">
          <input type="checkbox" checked={useCommon} onChange={(e) => { setUseCommon(e.target.checked); if (e.target.checked) applyCfg(common); }} className="accent-amber-500" />
          Utiliser la configuration commune pour <b>{target}</b> (décoche pour lui donner ses propres pages et tâches)
        </label>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <label className="text-xs text-zinc-400 space-y-1">
          <span>Titre</span>
          <input value={cfg.title || ""} disabled={!editable} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} data-testid="admin-space-title-input"
            className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 disabled:opacity-60" />
        </label>
        <label className="text-xs text-zinc-400 space-y-1">
          <span>Tâches à faire (une par ligne)</span>
          <textarea rows={3} value={tasksText} disabled={!editable} onChange={(e) => setTasksText(e.target.value)} data-testid="admin-space-tasks-input"
            className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 disabled:opacity-60" placeholder="Vérifier les inscriptions du jour…" />
        </label>
      </div>
      <label className="text-xs text-zinc-400 space-y-1 block mb-4">
        <span>Message d'accueil</span>
        <textarea rows={3} value={cfg.welcome_message || ""} disabled={!editable} onChange={(e) => setCfg({ ...cfg, welcome_message: e.target.value })} data-testid="admin-space-message-input"
          className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 disabled:opacity-60" />
      </label>

      <div className="text-xs text-zinc-400 mb-2">Pages ouvertes — <b className="text-amber-300" data-testid="admin-space-count">{allowed.size}</b> cochée(s)</div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {NAV_GROUPS.map((g) => {
          const n = g.items.filter((it) => allowed.has(it.to)).length;
          return (
            <div key={g.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`admin-space-group-${g.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-300">{g.label} <span className="text-zinc-600">({n}/{g.items.length})</span></div>
                {editable && (
                  <button type="button" onClick={() => toggleGroup(g, n < g.items.length)} className="text-[10px] text-amber-400 hover:underline" data-testid={`admin-space-group-toggle-${g.id}`}>
                    {n < g.items.length ? "Tout" : "Aucun"}
                  </button>
                )}
              </div>
              <div className="space-y-1 max-h-44 overflow-auto pr-1">
                {g.items.map((it) => (
                  <label key={it.to} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer hover:text-zinc-100">
                    <input type="checkbox" checked={allowed.has(it.to)} disabled={!editable} onChange={() => togglePath(it.to)} data-testid={`admin-space-path-${it.to.replace(/\//g, "-")}`} className="accent-amber-500" />
                    <span className="truncate">{it.label}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
